import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../context/AuthContext';

const s = {
  page: { minHeight: '100vh', background: '#0a0a0a', color: '#fff', fontFamily: 'system-ui, -apple-system, sans-serif' },
  loginWrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  loginCard: { background: '#111', border: '1px solid rgba(26,107,74,0.35)', borderRadius: 20, padding: '36px 32px', maxWidth: 380, width: '100%' },
  h1: { fontFamily: 'Georgia, serif', fontSize: 22, margin: '0 0 20px', textAlign: 'center' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#a8a8a8', marginBottom: 6 },
  input: { width: '100%', padding: '13px 14px', borderRadius: 9, border: '1px solid #ddd', fontSize: 15, marginBottom: 14, boxSizing: 'border-box', outline: 'none' },
  button: { width: '100%', padding: '14px', borderRadius: 9, border: 'none', background: '#1a6b4a', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  buttonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  error: { fontSize: 13, padding: '10px 14px', borderRadius: 8, marginBottom: 14, background: 'rgba(248,113,113,0.12)', color: '#f87171' },

  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  headerTitle: { fontFamily: 'Georgia, serif', fontSize: 18 },
  logoutBtn: { background: 'none', border: 'none', color: '#9b9b9b', fontSize: 13, cursor: 'pointer' },
  body: { maxWidth: 900, margin: '0 auto', padding: '32px 24px' },
  sectionLabel: { fontSize: 12, color: '#8a8a8a', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginBottom: 12 },
  form: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 32, background: '#111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 20 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 140px', minWidth: 0 },
  fieldLabel: { fontSize: 12, color: '#8a8a8a' },
  smallInput: { background: '#0a0a0a', border: '1px solid #2a2a2a', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  select: { background: '#0a0a0a', border: '1px solid #2a2a2a', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  tableWrap: { overflowX: 'auto' },
  publishBtn: { background: '#1a6b4a', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  status: { fontSize: 13, color: '#9b9b9b', marginBottom: 20, minHeight: 18 },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', color: '#8a8a8a', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  td: { padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  closeBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#c9c9c9', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginRight: 8 },
  deleteBtn: { background: 'none', border: 'none', color: '#f87171', fontSize: 12, cursor: 'pointer' },
};

export default function TraderDesk() {
  const [token, setToken] = useState(() => localStorage.getItem('trader_token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState({ symbol: '', side: 'long', entry_price: '', leverage: '', notes: '' });
  const [status, setStatus] = useState('');

  const loadTrades = useCallback(() => {
    if (!token) return;
    fetch(`${API}/api/trade-room/admin/trades`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error('unauthorized');
        return r.json();
      })
      .then((d) => setTrades(d.trades || []))
      .catch(() => {
        localStorage.removeItem('trader_token');
        setToken(null);
      });
  }, [token]);

  useEffect(() => { loadTrades(); }, [loadTrades]);

  const login = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/trade-room/trader-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Login failed.');
        return;
      }
      localStorage.setItem('trader_token', data.token);
      setToken(data.token);
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }, [username, password]);

  const logout = () => {
    localStorage.removeItem('trader_token');
    setToken(null);
  };

  const publish = useCallback(async (e) => {
    e.preventDefault();
    if (!form.symbol || !form.entry_price) { setStatus('Symbol and entry price are required.'); return; }
    setStatus('Publishing...');
    try {
      const res = await fetch(`${API}/api/trade-room/admin/trades`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setStatus(data.error || 'Failed to publish.'); return; }
      setStatus('Published.');
      setForm({ symbol: '', side: 'long', entry_price: '', leverage: '', notes: '' });
      loadTrades();
    } catch {
      setStatus('Something went wrong.');
    }
  }, [form, token, loadTrades]);

  const closeTrade = useCallback(async (trade) => {
    const exitPrice = window.prompt(`Exit price for ${trade.symbol}?`);
    if (!exitPrice) return;
    const leverageMult = trade.leverage ? parseFloat(String(trade.leverage).replace(/x$/i, '')) || 1 : 1;
    const rawMovePct = ((Number(exitPrice) - Number(trade.entry_price)) / Number(trade.entry_price)) * 100 * (trade.side === 'short' ? -1 : 1);
    const calculatedPnl = (rawMovePct * leverageMult).toFixed(2);
    const exactInput = window.prompt(
      `Exact P/L % from BTCC (optional — paste the real number if you have it, or leave blank to use the calculated ${calculatedPnl}% at ${leverageMult}x leverage)`
    );
    const pnl = exactInput && exactInput.trim() !== '' ? Number(exactInput).toFixed(2) : calculatedPnl;
    const result = Number(pnl) > 0 ? 'win' : Number(pnl) < 0 ? 'loss' : 'breakeven';
    await fetch(`${API}/api/trade-room/admin/trades/${trade.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ exit_price: exitPrice, status: 'closed', result, pnl_percent: pnl }),
    });
    loadTrades();
  }, [token, loadTrades]);

  const deleteTrade = useCallback(async (id) => {
    if (!window.confirm('Delete this trade?')) return;
    await fetch(`${API}/api/trade-room/admin/trades/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    loadTrades();
  }, [token, loadTrades]);

  if (!token) {
    return (
      <div style={s.page}>
        <div style={s.loginWrap}>
          <div style={s.loginCard}>
            <h1 style={s.h1}>Trade Desk</h1>
            {error && <div style={s.error}>{error}</div>}
            <form onSubmit={login}>
              <label style={s.label} htmlFor="tu">Username</label>
              <input id="tu" style={s.input} value={username} onChange={(e) => setUsername(e.target.value)} required />
              <label style={s.label} htmlFor="tp">Password</label>
              <input id="tp" type="password" style={s.input} value={password} onChange={(e) => setPassword(e.target.value)} required />
              <button style={{ ...s.button, ...(busy ? s.buttonDisabled : {}) }} disabled={busy} type="submit">
                {busy ? 'Logging in...' : 'Log in'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.headerTitle}>Trade Desk</div>
        <button onClick={logout} style={s.logoutBtn}>Log out</button>
      </div>
      <div style={s.body}>
        <div style={s.sectionLabel}>Publish a Trade</div>
        <form onSubmit={publish} style={s.form}>
          <div style={s.field}>
            <span style={s.fieldLabel}>Symbol</span>
            <input style={s.smallInput} placeholder="BTC/USD" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} />
          </div>
          <div style={s.field}>
            <span style={s.fieldLabel}>Side</span>
            <select style={s.select} value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value })}>
              <option value="long">Long</option>
              <option value="short">Short</option>
            </select>
          </div>
          <div style={s.field}>
            <span style={s.fieldLabel}>Entry Price</span>
            <input style={s.smallInput} type="number" step="any" placeholder="60000" value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} />
          </div>
          <div style={s.field}>
            <span style={s.fieldLabel}>Leverage</span>
            <input style={s.smallInput} placeholder="100x" value={form.leverage} onChange={(e) => setForm({ ...form, leverage: e.target.value })} />
          </div>
          <div style={{ ...s.field, flex: '1 1 200px' }}>
            <span style={s.fieldLabel}>Notes</span>
            <input style={s.smallInput} placeholder="optional" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <button type="submit" style={s.publishBtn}>Publish</button>
        </form>
        <div style={s.status}>{status}</div>

        <div style={s.sectionLabel}>All Trades</div>
        <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Symbol</th>
              <th style={s.th}>Side</th>
              <th style={s.th}>Entry</th>
              <th style={s.th}>Exit</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Result</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => (
              <tr key={t.id}>
                <td style={s.td}>{t.symbol}</td>
                <td style={s.td}>{t.side}{t.leverage ? ` · ${/x$/i.test(String(t.leverage).trim()) ? t.leverage : `${t.leverage}x`}` : ''}</td>
                <td style={s.td}>${t.entry_price}</td>
                <td style={s.td}>{t.exit_price ? `$${t.exit_price}` : '—'}</td>
                <td style={s.td}>{t.status}</td>
                <td style={s.td}>{t.result ? `${t.result} (${t.pnl_percent}%)` : '—'}</td>
                <td style={s.td}>
                  {t.status !== 'closed' && <button onClick={() => closeTrade(t)} style={s.closeBtn}>Close</button>}
                  <button onClick={() => deleteTrade(t.id)} style={s.deleteBtn}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {trades.length === 0 && <p style={{ color: '#6b6b6b', textAlign: 'center', padding: 32 }}>No trades published yet.</p>}
      </div>
    </div>
  );
}
